/**
 * PWA utilities for handling standalone mode
 * In standalone mode, external links need special handling
 */

/**
 * Check if the app is running in standalone mode (PWA installed)
 */
export const isStandalone = (): boolean => {
  // Check if running in standalone mode (PWA installed)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  // Check if running from home screen on iOS
  if ((window.navigator as any).standalone === true) {
    return true;
  }
  return false;
};

/**
 * Check if the app is running in a browser (not standalone)
 */
export const isBrowser = (): boolean => {
  return !isStandalone();
};

/**
 * Safely redirect to an external URL
 * In standalone mode, this ensures the redirect works correctly
 * 
 * @param url - The URL to redirect to
 * @param options - Options for the redirect
 */
export const redirectToExternal = (
  url: string,
  options?: {
    /** Open in new window/tab instead of redirecting */
    openInNewWindow?: boolean;
    /** Fallback URL if redirect fails */
    fallbackUrl?: string;
  }
): void => {
  try {
    // Ensure URL is absolute
    const absoluteUrl = url.startsWith('http')
      ? url
      : url.startsWith('//')
        ? `https:${url}`
        : `https://${url}`;
    
    if (options?.openInNewWindow) {
      // Open in new window/tab
      window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Redirect in same window (for Stripe checkout, etc.)
      const before = window.location.href;
      window.location.href = absoluteUrl;

      // Safety timeout: if navigation didn't happen, fall back.
      if (options?.fallbackUrl) {
        setTimeout(() => {
          try {
            if (document.visibilityState !== 'visible') return;
            if (window.location.href !== before) return;
            window.location.href = options.fallbackUrl!;
          } catch {
            // ignore
          }
        }, 5000);
      }
    }
  } catch (error) {
    console.error('Error redirecting to external URL:', error);
    if (options?.fallbackUrl) {
      window.location.href = options.fallbackUrl;
    }
  }
};

/**
 * Open an external link safely
 * For external links that should open in a new tab/window
 */
export const openExternalLink = (url: string): void => {
  redirectToExternal(url, { openInNewWindow: true });
};



